import React from 'react';
import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';

interface SummaryCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
  onClick?: () => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon, title, value, subtitle, color, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Card
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: 1.5,
          height: '100%',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          boxShadow: 'none',
          bgcolor: 'background.paper',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: 'grey.50',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '1.4rem',
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 0.5,
          }}
        >
          {value}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: color || 'primary.main',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '& > *': { width: '18px !important', height: '18px !important' },
              '& svg': { width: 18, height: 18 },
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              lineHeight: 1,
            }}
          >
            {title}
          </Typography>
        </Box>
      </Card>
    );
  }

  // Desktop — original design
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: color
            ? `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`
            : 'linear-gradient(90deg, #B07A8F 0%, #C9A96E 100%)',
        },
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(176,122,143,0.10)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: color ? `${color}20` : 'primary.light',
          color: color || 'primary.main',
          flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: '1.5rem' },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default SummaryCard;
